import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import { TextField, Modal, Box } from '@mui/material';
import { useStyles } from '@/styles/templateInputStyles';
import Button from '@/components/CustomButtons/Button';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

export default function EmailTemplateForm({
  formInputs,
  onInputChange,
  labelStyle,
  disabled = false,
  logoData,
  type,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [preview, setPreview] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const closePreview = () => {
    setIsPreviewOpen(false);
  };

  const onPreview = async (e) => {
    try {
      e.preventDefault();
      const formData = new FormData();

      formData.append('body', formInputs.body || '');

      // Set the correct logo data based on use_business_unit_logo flag
      if (logoData.use_business_unit_logo) {
        // Don't append any logo file if using business unit logo
        formData.append('use_business_unit_logo', true);
      } else if (logoData.logo) {
        formData.append('logo', logoData.logo);
      }

      formData.append('include_cpd_logo', logoData.include_cpd_logo || false);
      formData.append(
        'use_business_unit_logo',
        logoData.use_business_unit_logo || false,
      );

      formData.append('subject', formInputs.subject || '');
      formData.append('heading', formInputs.heading || '');
      formData.append('cc', formInputs.cc || '');
      formData.append('bcc', formInputs.bcc || '');

      const response = await axios.post(
        API_BASE_URL + '/audiences/preview-template?type=' + (type || ''),
        formData,
      );
      setIsPreviewOpen(true);
      setPreview(response.data.html);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <form>
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '5px',
            }}>
            <label style={labelStyle}>{t('emailTemplates.form.subject')}</label>
            <TextField
              variant='outlined'
              className={classes.root}
              multiline
              inputProps={{
                style: {
                  fontSize: '14px',
                  lineHeight: '1.3',
                  width: '100%',
                },
              }}
              minRows={1}
              maxRows={2}
              name='subject'
              value={formInputs?.subject ?? ''}
              onChange={onInputChange}
              disabled={disabled}
            />
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '5px',
            }}>
            <label style={labelStyle}>{t('emailTemplates.form.heading')}</label>
            <TextField
              variant='outlined'
              className={classes.root}
              multiline
              inputProps={{
                style: {
                  fontSize: '14px',
                  lineHeight: '1.3',
                  width: '100%',
                },
              }}
              minRows={1}
              maxRows={2}
              name='heading'
              value={formInputs?.heading ?? ''}
              onChange={onInputChange}
              disabled={disabled}
            />
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '5px',
            }}>
            <label style={labelStyle}>{t('emailTemplates.form.body')}</label>
            <TextField
              variant='outlined'
              className={classes.root}
              multiline
              inputProps={{
                style: {
                  fontSize: '14px',
                  lineHeight: '1.3',
                  width: '100%',
                },
              }}
              minRows={9}
              maxRows={12}
              name='body'
              value={formInputs?.body ?? ''}
              onChange={onInputChange}
              disabled={disabled}
            />
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '5px',
            }}>
            <label style={labelStyle}>
              {t('emailTemplates.form.cc')}{' '}
              <small>{t('emailTemplates.form.commaSeparated')}</small>
            </label>
            <TextField
              variant='outlined'
              className={classes.root}
              multiline
              inputProps={{
                style: {
                  fontSize: '14px',
                  lineHeight: '1.3',
                  width: '100%',
                },
              }}
              minRows={1}
              maxRows={2}
              name='cc'
              value={formInputs?.cc ?? ''}
              onChange={onInputChange}
              disabled={disabled}
            />
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '5px',
            }}>
            <label style={labelStyle}>
              {t('emailTemplates.form.bcc')}{' '}
              <small>{t('emailTemplates.form.commaSeparated')}</small>
            </label>
            <TextField
              variant='outlined'
              className={classes.root}
              multiline
              inputProps={{
                style: {
                  fontSize: '14px',
                  lineHeight: '1.3',
                  width: '100%',
                },
              }}
              minRows={1}
              maxRows={2}
              name='bcc'
              value={formInputs?.bcc ?? ''}
              onChange={onInputChange}
              disabled={disabled}
            />
          </div>
        </GridItem>
      </GridContainer>

      {!disabled && (
        <div style={{ marginTop: '20px' }}>
          <Button
            variant='contained'
            color='primary'
            onClick={onPreview}
            type='button'>
            {t('emailTemplates.form.previewButton')}
          </Button>
        </div>
      )}

      <Modal
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        open={isPreviewOpen && preview !== ''}
        onClose={closePreview}>
        <Box
          style={{
            height: '90vh',
            background: '#fff',
            borderRadius: '8px',
            minWidth: '90vw',
            maxWidth: '800px',
          }}>
          <iframe
            style={{ outline: 'none', border: 'none' }}
            width='100%'
            height='90%'
            src={'data:text/html,' + preview}
          />
          <div
            style={{
              margin: 'auto',
              width: '100px',
              height: '100%',
              textAlign: 'center',
            }}>
            <Button color='primary' onClick={closePreview}>
              {t('emailTemplates.form.closeButton')}
            </Button>
          </div>
        </Box>
      </Modal>
    </form>
  );
}
